import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, Download, Star, CheckCircle, Shield, Wifi, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

export function MobileAppPage() {
  const { productSlug } = useParams();

  const mobileApps = {
    'shalteron': {
      title: 'Akıllı Cihaz Yönetimi',
      productTitle: 'Akıllı Cihaz Yönetimi',
      description: 'GSM Röle Kontrol Cihazınızı uzaktan kontrol edin. Mobil uygulamamız ile cihazlarınızı istediğiniz yerden yönetebilir, zamanlama ayarları yapabilir ve durumlarını takip edebilirsiniz.',
      icon: '📱',
      iconPath: '/akilli-cihaz-yonetimi-ikon.png',
      heroImagePath: '/akilli-cihaz-yonetimi-uygulama.png',
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.akilli_cihaz_yonetimi&pcampaignid=web_share',
      features: [
        'Uzaktan cihaz kontrolü',
        'Zamanlama ve programlama',
        'Gerçek zamanlı durum takibi',
        'SMS bildirimleri',
        'Güvenli bağlantı',
        'Kullanıcı dostu arayüz'
      ],
      screenshots: [
        '/app-screenshot-1.png',
        '/app-screenshot-2.png',
        '/app-screenshot-3.png'
      ],
      systemRequirements: [
        'Android 6.0 veya üzeri',
        'İnternet bağlantısı',
        'GSM cihazı aktif olmalı'
      ],
      rating: 4.8,
      downloads: '10K+',
      category: 'Otomasyon & Kontrol'
    },
    'frigolive': {
      title: 'Frigolive',
      productTitle: 'Frigolive',
      description: 'Frigolive sıcaklık takip cihazınızı mobil uygulamamız ile kolayca yönetin. Gerçek zamanlı sıcaklık verilerini takip edin, alarm ayarları yapın ve geçmiş verileri analiz edin.',
      icon: '🌡️',
      iconPath: '/frigolive-uygulama-icon.png',
      heroImagePath: '/frigolive-login.jpg',
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.frigolivellogger&pcampaignid=web_share',
      appStoreUrl: 'https://apps.apple.com/us/app/frigolive/id6754630034',
      features: [
        'Gerçek zamanlı sıcaklık takibi',
        'Alarm ve bildirim sistemi',
        'Geçmiş veri analizi',
        'Grafik ve raporlama',
        'Çoklu cihaz desteği',
        'Offline veri senkronizasyonu'
      ],
      screenshots: [
        '/app-screenshot-1.png',
        '/app-screenshot-2.png',
        '/app-screenshot-3.png'
      ],
      systemRequirements: [
        'Android 7.0 veya üzeri',
        'iOS 14.0 veya üzeri',
        'İnternet bağlantısı',
        'Frigolive cihazı aktif olmalı'
      ],
      rating: 4.7,
      downloads: '5K+',
      category: 'IoT & Sensörler'
    },
    'akilli-gecis-sistemi': {
      title: 'Akıllı Geçiş Sistemi',
      productTitle: 'Akıllı Geçiş Sistemi',
      description: 'Akıllı Geçiş Sistemi mobil uygulamamız ile giriş kontrolünüzü kolayca yönetin. QR kod okuyucu, RFID kart desteği ve detaylı raporlama özellikleri ile tam kontrol sağlayın.',
      icon: '🔐',
      iconPath: '/ilimera_ags_402x402.png',
      heroImagePath: '/akilli-gecis-sistemi-uygulama.png',
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.kartli_gecis_sistemi&pcampaignid=web_share',
      features: [
        'QR kod ve RFID kart okuyucu',
        'Gerçek zamanlı giriş takibi',
        'Detaylı raporlama sistemi',
        'Yoklama ve vardiya yönetimi',
        'Zaman dilimi kontrolü',
        'Çoklu kullanıcı desteği'
      ],
      screenshots: [
        '/app-screenshot-1.png',
        '/app-screenshot-2.png',
        '/app-screenshot-3.png'
      ],
      systemRequirements: [
        'Android 6.0 veya üzeri',
        'İnternet bağlantısı',
        'Akıllı Geçiş cihazı aktif olmalı'
      ],
      rating: 4.6,
      downloads: '8K+',
      category: 'Güvenlik Sistemleri'
    }
  };

  const app = mobileApps[productSlug as keyof typeof mobileApps];

  if (!app) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Uygulama Bulunamadı</h1>
        <p className="text-gray-600 mb-8">Aradığınız mobil uygulama mevcut değil.</p>
        <Link to="/urunler">
          <Button>
            <ArrowLeft className="mr-2" size={16} />
            Ürünlere Dön
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Link to="/urunler" className="hover:text-primary transition-colors">Ürünler</Link>
          <span>/</span>
          <Link to={`/urunler/${productSlug}`} className="hover:text-primary transition-colors">
            {app.productTitle}
          </Link>
          <span>/</span>
          <span className="text-foreground">Mobil Uygulama</span>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4 text-2xl overflow-hidden">
                {(app as any).iconPath ? (
                  <img src={(app as any).iconPath} alt={`${app.title} icon`} className="w-12 h-12 object-contain" />
                ) : (
                  <span>{app.icon}</span>
                )}
              </div>
              <div>
                <Badge className="mb-2 bg-muted text-green-800">
                  {app.category}
                </Badge>
                <h1 className="text-3xl font-bold text-foreground">{app.title}</h1>
              </div>
            </div>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              {app.description}
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90"
                onClick={() => window.open(app.downloadUrl, '_blank', 'noopener,noreferrer')}
              >
                <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                Play Store'dan İndir
              </Button>

              {(app as any).appStoreUrl && (
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => window.open((app as any).appStoreUrl, '_blank', 'noopener,noreferrer')}
                >
                  <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                    <path d="M16.365 1.43c0 1.14-.464 2.182-1.234 2.952-.787.787-1.826 1.267-2.904 1.186-.113-1.064.33-2.114 1.102-2.903.77-.788 1.957-1.358 3.036-1.235zM20.235 13.53c-.064-.13-2.45-1.327-2.55-4.02-.04-1.076.296-2.152 1.04-3.007-.513-.73-1.275-1.288-2.164-1.57-.233-.07-1.532-.293-2.892.616-.507.334-1.137.334-1.68 0-1.36-.91-2.66-.687-2.892-.615-.89.282-1.65.84-2.165 1.57.745.855 1.08 1.93 1.04 3.008-.1 2.692-2.484 3.89-2.547 4.018-.064.13-.607 2.195.46 4.316.403.79.91 1.488 1.51 2.08 1.062 1.05 2.26 1.05 2.75 1.05.255 0 .613-.08 1.02-.23.408-.15.88-.23 1.34-.23.45 0 .93.08 1.34.23.41.15.77.23 1.03.23.49 0 1.69 0 2.75-1.05.6-.59 1.11-1.29 1.51-2.08 1.07-2.12.52-4.18.46-4.317z"/>
                  </svg>
                  App Store'dan İndir
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-center">
            <div className="w-64 h-98 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center shadow-2xl overflow-hidden">
              {(app as any).heroImagePath ? (
                <img src={(app as any).heroImagePath} alt={`${app.title} screenshot`} className="w-full h-full object-cover" />
              ) : (
                <Smartphone className="w-32 h-32 text-muted-foreground" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">Uygulama Özellikleri</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {app.features.map((feature, index) => (
            <Card key={index} className="p-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                  <CheckCircle className="text-primary" size={20} />
                </div>
                <span className="text-foreground font-medium">{feature}</span>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* System Requirements */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">Sistem Gereksinimleri</h2>
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {app.systemRequirements.map((requirement, index) => {
                const icons = [Shield, Shield, Wifi, Settings];
                const titles = ['İşletim Sistemi (Android)', 'İşletim Sistemi (iOS)', 'Bağlantı', 'Cihaz Durumu'];
                const Icon = icons[index];
                
                return (
                  <div key={index} className="flex items-center">
                    <Icon className="text-primary mr-3" size={24} />
                    <div>
                      <h3 className="font-semibold text-foreground">{titles[index]}</h3>
                      <p className="text-muted-foreground">{requirement}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Uygulamayı Hemen İndirin
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {app.productTitle} cihazınızı mobil uygulamamız ile kolayca yönetin. 
            Hemen indirin ve deneyim farkını yaşayın.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90"
              onClick={() => window.open(app.downloadUrl, '_blank', 'noopener,noreferrer')}
            >
              <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Play Store'dan İndir
            </Button>
            {(app as any).appStoreUrl && (
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90"
                onClick={() => window.open((app as any).appStoreUrl, '_blank', 'noopener,noreferrer')}
              >
                <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                  <path d="M16.365 1.43c0 1.14-.464 2.182-1.234 2.952-.787.787-1.826 1.267-2.904 1.186-.113-1.064.33-2.114 1.102-2.903.77-.788 1.957-1.358 3.036-1.235zM20.235 13.53c-.064-.13-2.45-1.327-2.55-4.02-.04-1.076.296-2.152 1.04-3.007-.513-.73-1.275-1.288-2.164-1.57-.233-.07-1.532-.293-2.892.616-.507.334-1.137.334-1.68 0-1.36-.91-2.66-.687-2.892-.615-.89.282-1.65.84-2.165 1.57.745.855 1.08 1.93 1.04 3.008-.1 2.692-2.484 3.89-2.547 4.018-.064.13-.607 2.195.46 4.316.403.79.91 1.488 1.51 2.08 1.062 1.05 2.26 1.05 2.75 1.05.255 0 .613-.08 1.02-.23.408-.15.88-.23 1.34-.23.45 0 .93.08 1.34.23.41.15.77.23 1.03.23.49 0 1.69 0 2.75-1.05.6-.59 1.11-1.29 1.51-2.08 1.07-2.12.52-4.18.46-4.317z"/>
                </svg>
                App Store'dan İndir
              </Button>
            )}
            <Link to={`/urunler/${productSlug}`}>
              <Button className="dark:bg-background" size="lg" variant="outline">
                Ürün Detaylarına Dön
                <ArrowLeft className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
